import { useEffect, useState } from 'react';

import { useQuery, useSubscription } from '@apollo/client';
import { Edit } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  DialogActions,
  Box,
  DialogContent,
  FormControl,
  Stack,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { ClubLogo, Modal, ModalLink } from '@scorenco/components';
import {
  useTranslation,
  Types,
  useUpdateEngagementRules,
  ChallengeListenTeamNameDocument,
} from '@scorenco/core';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';

import { T_KEYS } from '@/translations';

import {
  GetPoolsBeforeThisRuleDocument,
  GetEngagementDetailsDocument,
  GetPrecedentsRulesWithPoolDocument,
  GetCupGamesDocument,
} from '../index.query.generated';

export const TeamEngagementChoice = ({ engagement, phase }) => {
  const [enableSubscription, setEnableSubscription] = useState(false);

  useSubscription(ChallengeListenTeamNameDocument, {
    variables: { id: engagement.id },
    skip: !engagement.id || !enableSubscription,
    context: { role: 'club' },
  });

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ marginTop: '0.5rem', minWidth: '12rem' }}
    >
      <ClubLogo
        style={{
          height: '1.5rem',
          width: '1.25rem',
          marginRight: '1rem',
        }}
        logo={engagement.team?.logo}
        name={engagement.team?.name}
        shieldColor="home"
      />
      {engagement?.team?.name || engagement?.name}
      <ModalLink
        Modal={TeamEngagementChoiceModal}
        modalProps={{
          phase,
          engagement_id: engagement.id,
          onClose: () => {
            setTimeout(() => {
              setEnableSubscription(false);
            }, 10000);
          },
        }}
        onClick={() => setEnableSubscription(true)}
      >
        <IconButton size="small" sx={{ marginLeft: 'auto' }}>
          <Edit fontSize="0.75rem" />
        </IconButton>
      </ModalLink>
    </Stack>
  );
};
const PositionFromPool = ({ formik, phase }) => {
  const getPools = useQuery(GetPoolsBeforeThisRuleDocument, {
    variables: {
      rule_rank: phase.rule.rank,
      challenge_id: phase.rule.challenge_id,
    },
    context: {
      role: 'club',
    },
  });

  return (
    <>
      <FormControl sx={{ width: '100%' }}>
        <InputLabel shrink={true}>Choix de la poule</InputLabel>
        <Select
          id="previous_phase_id"
          name="previous_phase_id"
          value={formik.values.previous_phase_id}
          onChange={formik.handleChange}
          label="Choix de la poule"
          notched={true}
        >
          {getPools.data?.tournaments_phase.map((pool) => (
            <MenuItem key={pool.id} value={pool.id}>
              {pool.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ width: '100%' }}>
        <InputLabel shrink={true}>
          Rang de cette équipe dans la poule
        </InputLabel>
        <Select
          id="previous_phase_rank"
          name="previous_phase_rank"
          value={formik.values.previous_phase_rank}
          onChange={formik.handleChange}
          label="Rang de cette équipe dans la poule"
          disabled={!formik.values.previous_phase_id}
          notched={true}
        >
          {getPools.data?.tournaments_phase?.[
            getPools.data?.tournaments_phase.findIndex(
              (pool) => pool.id === formik.values.previous_phase_id
            )
          ]?.engagements.map((engagement, index) => (
            <MenuItem key={index + 1} value={index + 1}>
              {index + 1}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

const PositionFromAllPools = ({ formik, phase }) => {
  const getRules = useQuery(GetPrecedentsRulesWithPoolDocument, {
    variables: {
      rule_rank: phase.rule.rank,
      challenge_id: phase.rule.challenge_id,
    },
    context: {
      role: 'club',
    },
  });

  return (
    <>
      <FormControl sx={{ width: '100%' }}>
        <InputLabel shrink={true}>Choix de la phase</InputLabel>
        <Select
          id="previous_challenge_rule_id"
          name="previous_challenge_rule_id"
          value={formik.values.previous_challenge_rule_id}
          onChange={formik.handleChange}
          label="Choix de la poule"
          notched={true}
        >
          {getRules.data?.tournaments_challenge_rule.map((rule) => (
            <MenuItem key={rule.id} value={rule.id}>
              {rule.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ width: '100%' }}>
        <InputLabel shrink={true}>
          Rang de cette équipe dans sa poule
        </InputLabel>
        <Select
          id="previous_phase_rank"
          name="previous_phase_rank"
          value={formik.values.previous_phase_rank}
          onChange={formik.handleChange}
          label="Rang de cette équipe dans la poule"
          disabled={!formik.values.previous_challenge_rule_id}
          notched={true}
        >
          {Array.from({
            length:
              getRules.data?.tournaments_challenge_rule?.[
                getRules.data?.tournaments_challenge_rule.findIndex(
                  (rule) => rule.id === formik.values.previous_challenge_rule_id
                )
              ]?.phases?.[0]?.engagements_aggregate?.aggregate?.count,
          })?.map((_, index) => (
            <MenuItem key={index + 1} value={index + 1}>
              {index + 1}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ width: '100%' }}>
        <InputLabel shrink={true}>
          Rang de cette équipe parmis les équipes à la même position
        </InputLabel>
        <Select
          id="previous_challenge_rule_rank"
          name="previous_challenge_rule_rank"
          value={formik.values.previous_challenge_rule_rank}
          onChange={formik.handleChange}
          label="Rang de cette équipe parmis les équipes à la même position"
          disabled={
            !formik.values.previous_challenge_rule_id ||
            !formik.values.previous_phase_rank
          }
          notched={true}
        >
          {Array.from({
            length:
              getRules.data?.tournaments_challenge_rule?.[
                getRules.data?.tournaments_challenge_rule.findIndex(
                  (rule) => rule.id === formik.values.previous_challenge_rule_id
                )
              ]?.phases_aggregate?.aggregate?.count,
          })?.map((_, index) => (
            <MenuItem key={index + 1} value={index + 1}>
              {index + 1}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

const WinnerOrLoserOfMatch = ({ formik, phase }) => {
  const [winner, setWinner] = useState<Boolean>(
    !!formik.values.winner_of_game_id
  );
  const getGames = useQuery(GetCupGamesDocument, {
    variables: {
      challenge_id: phase.rule.challenge_id,
    },
    context: {
      role: 'club',
    },
  });

  return (
    <>
      <FormControl sx={{ width: '100%' }}>
        <InputLabel shrink={true}>Choix du match</InputLabel>
        <Select
          value={
            winner
              ? formik.values.winner_of_game_id
              : formik.values.loser_of_game_id
          }
          onChange={(e) => {
            if (winner) {
              formik.setFieldValue('winner_of_game_id', e.target.value);
            } else {
              formik.setFieldValue('loser_of_game_id', e.target.value);
            }
          }}
          label="Choix du match"
          notched={true}
        >
          {getGames.data?.tournaments_game.map((game) => (
            <MenuItem key={game.id} value={game.id}>
              {game.name} - {game.home_engagement?.phase?.name} -{' '}
              {game.home_engagement?.phase?.rule?.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Select
        value={winner}
        onChange={(e) => {
          setWinner(e.target.value as Boolean);
          const winner_id = formik.values.winner_of_game_id;
          const loser_id = formik.values.loser_of_game_id;
          formik.setFieldValue('winner_of_game_id', loser_id);
          formik.setFieldValue('loser_of_game_id', winner_id);
        }}
        notched={true}
      >
        <MenuItem value={true}>Vainqueur</MenuItem>
        <MenuItem value={false}>Perdant</MenuItem>
      </Select>
    </>
  );
};

type TeamEngagementChoiceModalProps = {
  phase: Types.Tournaments_Phase;
  engagement_id: number;
  closeModal: () => void;
};

const TeamEngagementChoiceModal = ({
  phase,
  engagement_id,
  onClose,
  closeModal,
}: TeamEngagementChoiceModalProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [choice, setChoice] = useState<1 | 2 | 3>(1);
  const updateEngagementRules = useUpdateEngagementRules();

  const getEngagementDetails = useQuery(GetEngagementDetailsDocument, {
    variables: {
      id: engagement_id,
    },
    context: {
      role: 'club',
    },
  });

  const engagementDetails =
    getEngagementDetails.data?.tournaments_engagement_by_pk;

  useEffect(() => {
    if (engagementDetails) {
      setChoice(
        engagementDetails.previous_phase_id &&
          engagementDetails.previous_phase_rank
          ? 1
          : engagementDetails.previous_challenge_rule_id &&
              engagementDetails.previous_challenge_rule_rank &&
              engagementDetails.previous_phase_rank
            ? 2
            : engagementDetails.winner_of_game_id ||
                engagementDetails.loser_of_game_id
              ? 3
              : 1
      );
    }
  }, [engagementDetails]);

  const formik = useFormik({
    initialValues: {
      engagement_id: engagement_id,
      previous_phase_id: engagementDetails?.previous_phase_id || null,
      previous_phase_rank: engagementDetails?.previous_phase_rank || null,
      winner_of_game_id: engagementDetails?.winner_of_game_id || null,
      loser_of_game_id: engagementDetails?.loser_of_game_id || null,
      previous_challenge_rule_id:
        engagementDetails?.previous_challenge_rule_id || null,
      previous_challenge_rule_rank:
        engagementDetails?.previous_challenge_rule_rank || null,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await updateEngagementRules.execute({
          engagementId: values.engagement_id,
          _set: {
            previous_phase_id: values.previous_phase_id,
            previous_phase_rank: values.previous_phase_rank,
            winner_of_game_id: values.winner_of_game_id,
            loser_of_game_id: values.loser_of_game_id,
            previous_challenge_rule_id: values.previous_challenge_rule_id,
            previous_challenge_rule_rank: values.previous_challenge_rule_rank,
          },
        });

        enqueueSnackbar(t(T_KEYS.SUCCESS_ADD), {
          variant: 'success',
        });
        onClose();
        closeModal();
        formik.resetForm();
      } catch (error) {
        console.error('error: ', error);
        enqueueSnackbar(t(T_KEYS.ERROR_MESSAGE), {
          variant: 'error',
        });
      }
    },
  });

  if (getEngagementDetails.loading)
    return (
      <Modal title="Paramétrer l'équipe" closeModal={closeModal}>
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </DialogContent>
      </Modal>
    );

  return (
    <Modal title="Paramétrer l'équipe" closeModal={closeModal}>
      <Box component="form" onSubmit={formik.handleSubmit}>
        <DialogContent
          style={{
            paddingTop: '1rem',
          }}
        >
          <Stack spacing={3}>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel>
                Quel mode de choix d'équipe souhaitez-vous ?
              </InputLabel>
              <Select
                value={choice}
                onChange={(e) => {
                  setChoice(e.target.value as 1 | 2 | 3);
                  formik.setFieldValue('previous_phase_id', null);
                  formik.setFieldValue('previous_phase_rank', null);
                  formik.setFieldValue('previous_challenge_rule_id', null);
                  formik.setFieldValue('previous_challenge_rule_rank', null);
                  formik.setFieldValue('winner_of_game_id', null);
                  formik.setFieldValue('loser_of_game_id', null);
                }}
                label="Quel mode de choix d'équipe souhaitez-vous ?"
              >
                <MenuItem value={1}>
                  Position d'une équipe dans une poule <i>(par défaut)</i>
                </MenuItem>
                <MenuItem value={2}>
                  Position d'une équipe parmi toutes les poules d'une phase (ex
                  : 2ème meilleur 3ème)
                </MenuItem>
                <MenuItem value={3}>
                  Vainqueur ou perdant d'un match de tour de coupe
                </MenuItem>
              </Select>
            </FormControl>
            {choice === 1 && <PositionFromPool formik={formik} phase={phase} />}
            {choice === 2 && (
              <PositionFromAllPools formik={formik} phase={phase} />
            )}
            {choice === 3 && (
              <WinnerOrLoserOfMatch formik={formik} phase={phase} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ m: 0, p: 1, justifyContent: 'center' }}>
          <LoadingButton
            type="submit"
            variant="contained"
            color="secondary"
            loading={updateEngagementRules.status.loading}
          >
            {t(T_KEYS.EDIT)}
          </LoadingButton>
        </DialogActions>
      </Box>
    </Modal>
  );
};
